import { CANVAS_WIDTH, CANVAS_HEIGHT } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { SOLO_SELECT_MODES } from "../../game/soloModes";
import type { SoloMode } from "../../game/types";
import type { drawButton } from "../ui";
import {
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  mFont,
  soloSelectDescription,
  soloModeLockCaption,
} from "../renderConstants";

// ── Mutable slide state (owned by caller, mutated in-place) ─────────

export interface SoloSlideState {
  soloPrevIdx: number;
  soloPrevMode: SoloMode | null;
  soloSlideStartMs: number;
  soloSlideDir: number;
}

// ── Dependency interface ────────────────────────────────────────────

export interface SoloSelectDeps {
  ctx: CanvasRenderingContext2D;
  slideState: SoloSlideState;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCircleArrowButton: (rect: Rect, dir: -1 | 1) => void;
  drawSoloSelectorLogo: (mode: SoloMode, rect: Rect, mobile: boolean) => void;
  wrapTextToWidth: (text: string, maxWidth: number, maxLines: number) => string[];
  updateHoverAnimations: (rects: Rect[]) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawSoloSelectScreen(deps: SoloSelectDeps, model: RenderModel): UiRects {
  const {
    ctx,
    slideState,
    drawBtn,
    drawCircleCloseButton,
    drawCircleArrowButton,
    drawSoloSelectorLogo,
    wrapTextToWidth,
    updateHoverAnimations,
  } = deps;
  const mobile = isMobileViewport();
  const centerX = CANVAS_WIDTH / 2;
  const carouselModes = SOLO_SELECT_MODES;
  const idx = model.soloCarouselIndex;
  const mode = carouselModes[idx % carouselModes.length]!;
  const description = soloSelectDescription(mode);
  const lockState = model.soloModeLocks[mode];
  const locked = !lockState.unlocked;
  const lockCaption = locked ? soloModeLockCaption(lockState) : "";

  // Detect index change for slide animation
  if (slideState.soloPrevIdx !== idx && slideState.soloPrevIdx >= 0) {
    slideState.soloPrevMode = carouselModes[slideState.soloPrevIdx % carouselModes.length] ?? null;
    const diff = idx - slideState.soloPrevIdx;
    if (diff === 1 || diff === -(carouselModes.length - 1)) {
      slideState.soloSlideDir = 1;
    } else {
      slideState.soloSlideDir = -1;
    }
    slideState.soloSlideStartMs = performance.now();
  }
  slideState.soloPrevIdx = idx;

  // Slide animation progress
  const slideDur = 300;
  const slideElapsed = performance.now() - slideState.soloSlideStartMs;
  const slideT = Math.min(1, slideElapsed / slideDur);
  const slideEased = 1 - (1 - slideT) * (1 - slideT); // easeOutQuad
  const isSliding = slideT < 1;
  const slideRange = CANVAS_WIDTH * 0.6;

  ctx.save();

  // Measure content block height to vertically center
  const logoH = mobile ? 140 : 160;
  const descGap = 12;
  const descFontPx = mobile ? 5.75 : 6.75;
  const descLineH = descFontPx * 1.72;
  ctx.font = mFont("normal", descFontPx);
  const descLines = wrapTextToWidth(description, mobile ? CANVAS_WIDTH - 92 : CANVAS_WIDTH - 120, 4);
  const descBlockH = descLines.length * descLineH;
  const lockFontPx = mobile ? 4.5 : 5;
  const lockLineH = lockFontPx * 1.35;
  ctx.font = mFont("bold", lockFontPx);
  const lockLines = locked ? wrapTextToWidth(lockCaption, mobile ? CANVAS_WIDTH - 110 : CANVAS_WIDTH - 150, 2) : [];
  const lockBlockH = lockLines.length * lockLineH;
  const lockGap = locked ? (mobile ? 10 : 12) : 0;
  const btnGap = mobile ? 22 : 24;
  const playH = mobile ? 44 : 36;
  const dotGap2 = mobile ? 28 : 30;
  const dotSize = 6;
  const totalH = logoH + descGap + descBlockH + lockGap + lockBlockH + btnGap + playH + dotGap2 + dotSize;
  const startY = Math.max(30, Math.floor((CANVAS_HEIGHT - totalH) / 2));

  // Close button (circle with X) top-right
  const closeRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(closeRect);

  // Mode logo centered and large
  const logoW = mobile ? 220 : 240;
  const logoY = startY;

  // Helper to draw a mode's logo + description at an X offset
  const drawModeContent = (drawMode: SoloMode, offX: number, alpha: number) => {
    const isLocked = !model.soloModeLocks[drawMode].unlocked;
    ctx.save();
    ctx.globalAlpha = alpha;
    if (isLocked) ctx.filter = "grayscale(0.85) brightness(0.5)";
    const logoRect: Rect = { x: centerX - logoW / 2 + offX, y: logoY, w: logoW, h: logoH };
    drawSoloSelectorLogo(drawMode, logoRect, mobile);
    if (isLocked) ctx.filter = "none";
    const drawDesc = soloSelectDescription(drawMode);
    ctx.font = mFont("normal", descFontPx);
    const drawDescLines = wrapTextToWidth(drawDesc, mobile ? CANVAS_WIDTH - 92 : CANVAS_WIDTH - 120, 4);
    let dy = logoY + logoH + descGap;
    ctx.fillStyle = "#c0daf0";
    ctx.font = mFont("normal", descFontPx);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (const line of drawDescLines) {
      ctx.fillText(line, centerX + offX, dy);
      dy += descLineH;
    }
    ctx.restore();
  };

  // Draw old mode sliding out
  if (isSliding && slideState.soloPrevMode != null) {
    const oldOffX = -slideEased * slideState.soloSlideDir * slideRange;
    drawModeContent(slideState.soloPrevMode, oldOffX, 1 - slideEased);
  }

  // Draw current mode sliding in (or static)
  const newOffX = isSliding ? (1 - slideEased) * slideState.soloSlideDir * slideRange : 0;
  drawModeContent(mode, newOffX, isSliding ? slideEased : 1);

  // Description bottom Y (for layout below -- use static position)
  const descBottomY = logoY + logoH + descGap + descBlockH;

  // Left/right arrow buttons (flanking logo, same size as close button)
  const arrowSize = uiCloseSize();
  const arrowY = logoY + logoH / 2 - arrowSize / 2;
  const prevRect: Rect = { x: UI_EDGE_PAD, y: arrowY, w: arrowSize, h: arrowSize };
  const nextRect: Rect = { x: CANVAS_WIDTH - UI_EDGE_PAD - arrowSize, y: arrowY, w: arrowSize, h: arrowSize };
  drawCircleArrowButton(prevRect, -1);
  drawCircleArrowButton(nextRect, 1);

  let lockY = descBottomY + lockGap;
  if (locked) {
    ctx.fillStyle = lockState.signInRequired ? "#ffd3d6" : "#ffe5a8";
    ctx.font = mFont("bold", lockFontPx);
    for (const line of lockLines) {
      ctx.fillText(line, centerX, lockY);
      lockY += lockLineH;
    }
  }

  // CTA button
  const playW = mobile ? 160 : 180;
  const playY = descBottomY + lockGap + lockBlockH + btnGap;
  const playRect: Rect = { x: centerX - playW / 2, y: playY, w: playW, h: playH };
  drawBtn(playRect, locked ? `UNLOCKS LV ${lockState.requiredLevel}` : "PLAY", {
    fill: locked ? "rgba(30,20,50,0.85)" : "rgba(20,36,54,0.85)",
    stroke: locked ? "rgba(108,98,133,0.6)" : "rgba(108,200,255,0.6)",
    text: locked ? "#cfc8e7" : "#e9f7ff",
    font: mFont("bold", 5),
    variant: "lobby",
  });

  // Dot indicators at bottom
  const dotGapH = 10;
  const dotsWidth = carouselModes.length * dotSize + (carouselModes.length - 1) * dotGapH;
  const dotsStartX = centerX - dotsWidth / 2;
  const dotsY = playY + playH + dotGap2;
  for (let i = 0; i < carouselModes.length; i += 1) {
    const dx = dotsStartX + i * (dotSize + dotGapH);
    ctx.beginPath();
    ctx.arc(dx + dotSize / 2, dotsY + dotSize / 2, dotSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = i === (idx % carouselModes.length) ? "#ffffff" : "rgba(255,255,255,0.3)";
    ctx.fill();
  }

  ctx.restore();

  const hoverRects: Rect[] = [closeRect, prevRect, nextRect, playRect];
  updateHoverAnimations(hoverRects);

  return {
    soloSelectBack: closeRect,
    soloCarouselPrev: prevRect,
    soloCarouselNext: nextRect,
    soloSelectPlay: playRect,
  };
}
